import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { PanelActionFooter, PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { FormulaEditor, PreviewBox, PreviewLine, Required } from "pages/PayRoll/Salary/Shared/salaryUi";
import { FormulaChip, SwitchLine, formatInr } from "pages/PayRoll/Shared/statutoryUi";
import { formulaReads } from "pages/PayRoll/Shared/statutoryConfigData";
import {
  FORMULA_FUNCTIONS,
  REQUIRED_CODES,
  calculationCodesFor,
  checkLwfFormula,
  nextDeductionMonth,
  runCalculation,
  tableFormulaOf,
  testEmployeesFor,
} from "./lwfConfigData";
import { checkLwfFormulaOnServer, testLwfEmployee } from "./lwfConfigApi";

// Calculation component drawer (LWF_WAGE and any extra wage the company
// adds) with formula check and Test on employee. Employee LWF / Employer
// LWF are fixed: they read the band, so they open read-only.
// With `server` ({ domain, companyId, siteId, employees }) the formula is
// also checked by POST config/formula/check/ and the test runs on real
// employees through POST config/test-employee/ with the unsaved rule.
// PT passes its own calls in `server` (checkFormula, testEmployee — module
// functions, so their identity never changes), the month to test in and
// the wage code to show.

const SERVER_DELAY = 600;

const emptyComponent = () => ({ code: "", name: "", kind: "CALCULATION", description: "", formula: "", used_for: "", status: "Active" });

// Reused by PT: requiredCodes, itemLabel and fixedNote set its own wording.
export const LwfComponentPanel = ({
  open,
  mode,
  component,
  form,
  siteName,
  readOnly,
  onClose,
  onSave,
  onDelete,
  server,
  requiredCodes = REQUIRED_CODES,
  itemLabel = "LWF",
  fixedNote = "Fixed by LWF: the amount comes from the Contribution Bands for the band the LWF wage falls in. Change the bands to change the amount.",
}) => {
  const [draft, setDraft] = useState(() => component || emptyComponent());
  const [showErrors, setShowErrors] = useState(false);
  const sampleEmployees = useMemo(() => testEmployeesFor(siteName), [siteName]);
  const employees = server ? server.employees || [] : sampleEmployees;
  const [employeeId, setEmployeeId] = useState(server ? "" : sampleEmployees[0]?.id);
  const [serverCheck, setServerCheck] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [serverTest, setServerTest] = useState({ loading: false, error: "", data: null });

  useEffect(() => {
    if (!open) return;
    setDraft(component ? { ...component } : emptyComponent());
    setShowErrors(false);
    setServerCheck(null);
    setServerTest({ loading: false, error: "", data: null });
  }, [open, component]);

  // Real employees: the first one once the list arrives.
  const firstEmployeeId = server?.employees?.[0]?.id;
  useEffect(() => {
    if (open && server && !employeeId && firstEmployeeId) setEmployeeId(firstEmployeeId);
  }, [open, server, employeeId, firstEmployeeId]);

  const adding = mode === "add";
  const fixed = Boolean(draft.fixed);
  const locked = readOnly || fixed;
  const required = requiredCodes.includes(draft.code) && !adding;
  const others = useMemo(() => form.components.filter((item) => adding || item.code !== component?.code), [form.components, adding, component]);
  const localCheck = useMemo(() => (fixed ? { valid: true } : checkLwfFormula(draft.formula, others, draft)), [fixed, draft, others]);

  // The server's formula check once the formula passes the check here.
  const serverDomain = server?.domain;
  const serverCompanyId = server?.companyId;
  const checkFormula = server?.checkFormula || checkLwfFormulaOnServer;
  const testEmployee = server?.testEmployee || testLwfEmployee;
  const checkable = Boolean(server) && open && !locked && localCheck.valid && Boolean(String(draft.formula).trim());
  useEffect(() => {
    if (!checkable) {
      setServerCheck(null);
      return undefined;
    }
    // The local check shows until the server answers for this formula.
    setServerCheck(null);
    const request = new AbortController();
    const timer = setTimeout(() => {
      checkFormula({ domain: serverDomain, companyId: serverCompanyId, code: draft.code, formula: String(draft.formula).trim(), signal: request.signal })
        .then((data) => setServerCheck({ valid: Boolean(data?.valid), message: String(data?.message || "").replace(/^[✓√]\s*/, "") || (data?.valid ? "Formula is valid" : "Formula is not valid") }))
        .catch((err) => {
          if (err.name !== "AbortError") setServerCheck({ valid: false, message: err.message });
        });
    }, SERVER_DELAY);
    return () => {
      clearTimeout(timer);
      request.abort();
    };
  }, [checkable, checkFormula, serverDomain, serverCompanyId, draft.code, draft.formula]);
  const check = !localCheck.valid || !serverCheck ? localCheck : serverCheck;

  const set = (key) => (value) => setDraft((current) => ({ ...current, [key]: key === "code" ? String(value).toUpperCase() : value }));

  const errors = {};
  if (!fixed) {
    if (!String(draft.name).trim()) errors.name = "Enter a name";
    if (adding) {
      if (!/^[A-Z][A-Z0-9_]{1,19}$/.test(draft.code)) errors.code = "2–20 characters: capitals, digits and _ , starting with a letter";
      else if (form.components.some((item) => item.code === draft.code)) errors.code = "This code is already used in this version";
    }
    if (!check.valid) errors.formula = check.message;
  }
  const error = (key) => (showErrors ? errors[key] : undefined);

  const employee = employees.find((item) => item.id === employeeId) || employees[0];
  const runnable = !fixed && check.valid && /^[A-Z][A-Z0-9_]{1,19}$/.test(draft.code);
  // The rule with this component as it is now (added or replaced).
  const own = { ...draft, status: "Active" };
  const testedComponents = adding ? [...form.components, own] : form.components.map((item) => (item.code === own.code ? own : item));
  const test = useMemo(() => {
    if (server || !runnable || !employee) return null;
    return runCalculation(testedComponents, employee, own);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server, runnable, draft, form.components, employee, adding]);

  // Real employee: POST config/test-employee/ with the unsaved rule.
  const serverSiteId = server?.siteId;
  const month = server?.month || (nextDeductionMonth(form) || moment()).format("YYYY-MM");
  const testKey = server && runnable && employeeId ? JSON.stringify({ ...form, components: testedComponents }) : "";
  useEffect(() => {
    if (!open || !testKey) {
      setServerTest({ loading: false, error: "", data: null });
      return undefined;
    }
    const request = new AbortController();
    setServerTest((current) => ({ ...current, loading: true, error: "" }));
    const timer = setTimeout(() => {
      testEmployee({ domain: serverDomain, companyId: serverCompanyId, siteId: serverSiteId, employeeId, month, form: JSON.parse(testKey), signal: request.signal })
        .then((data) => setServerTest({ loading: false, error: "", data }))
        .catch((err) => {
          if (err.name !== "AbortError") setServerTest({ loading: false, error: err.message, data: null });
        });
    }, SERVER_DELAY);
    return () => {
      clearTimeout(timer);
      request.abort();
    };
  }, [open, testKey, testEmployee, employeeId, month, serverDomain, serverCompanyId, serverSiteId]);

  const save = () => {
    setShowErrors(true);
    if (Object.keys(errors).length) return;
    onSave({ ...draft, name: draft.name.trim(), formula: String(draft.formula).trim() });
  };

  const title = fixed ? (draft.kind === "CALCULATION" ? "Calculation Component" : "Contribution Component") : adding ? "Add Calculation Component" : `${readOnly ? "" : "Edit "}Calculation Component`;

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title={title}
      subtitle={`${siteName} · ${form.name}`}
      bodySx={panelBodySx}
      footer={
        locked ? (
          <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
            <PrimaryButton onClick={onClose}>Close</PrimaryButton>
          </Mui.Stack>
        ) : (
          <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ width: "100%" }}>
            {!adding && !required && onDelete ? (
              <Mui.Button onClick={() => setDeleteOpen(true)} sx={{ color: "#DC2626", textTransform: "none", fontWeight: designWeights.semibold, mr: { sm: "auto" } }}>
                Delete component
              </Mui.Button>
            ) : null}
            <Mui.Box sx={{ flex: 1 }}>
              <PanelActionFooter onCancel={onClose} primaryLabel={adding ? "Add Component" : "Save"} primaryProps={{ onClick: save }} />
            </Mui.Box>
          </Mui.Stack>
        )
      }
    >
      <Mui.Stack spacing={2}>
        {fixed ? (
          <RequestInfoBanner body={fixedNote} />
        ) : readOnly ? (
          <RequestInfoBanner tone="warning" title="This version is locked." body="A payroll has used it. Create a new version to change components." />
        ) : null}

        <FormSection title="Component Details">
          <Field wide label={<>Component Name<Required /></>} helper="Plain words (e.g. LWF Wage)." error={error("name")}>
            <TextField value={draft.name} onChange={set("name")} disabled={locked} error={error("name")} placeholder="e.g. LWF Wage" />
          </Field>
          <Field
            wide
            label={<>Component Code<Required /></>}
            helper={adding ? "Used in formulas. Can't be changed after saving." : "Locked: other formulas use it."}
            error={error("code")}
          >
            <TextField value={draft.code} onChange={set("code")} disabled={!adding} error={error("code")} placeholder="e.g. LWF_WAGE" />
          </Field>
          <Field wide label="Description" helper="For your team. Not shown on the payslip.">
            <TextField value={draft.description} onChange={set("description")} disabled={locked} placeholder="What this wage is used for" />
          </Field>
          <Field wide label="Used for" helper="Shown in the components table.">
            <TextField value={draft.used_for} onChange={set("used_for")} disabled={locked} placeholder="e.g. Used to pick the band" />
          </Field>
        </FormSection>

        <FormSection
          title="Formula"
          info={fixed ? undefined : "Codes, numbers, + − × ÷, brackets, ROUND, ROUNDUP, ROUNDDOWN, MIN, MAX and IF. _E = earned amount for the month; without _E = monthly rate."}
        >
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            {locked ? (
              <FormulaChip formula={tableFormulaOf(draft)} />
            ) : (
              <FormulaEditor
                value={draft.formula}
                onChange={set("formula")}
                codes={calculationCodesFor(others, draft.code)}
                functions={FORMULA_FUNCTIONS}
                check={check}
                error={error("formula")}
              />
            )}
          </Mui.Box>
        </FormSection>

        {!fixed ? (
          <FormSection title="Test on employee" info="Preview only — nothing is saved.">
            <Field wide label="Employee" helper="Shows what this formula gives the employee.">

              <SelectField
                value={employeeId}
                onChange={(value) => setEmployeeId(value || employees[0]?.id || "")}
                options={employees.map((item) => ({ value: item.id, label: [item.name, item.code || (server ? "" : item.id), item.site].filter(Boolean).join(" · ") }))}
                placeholder={server && !server.employees ? "Loading employees…" : "Pick an employee"}
                disabled={Boolean(server) && !server.employees}
              />
            </Field>
            <Mui.Box sx={{ gridColumn: "1 / -1" }}>
              <PreviewBox>
                {server ? (
                  <ServerTestResult runnable={runnable} test={serverTest} noEmployees={Boolean(server.employees) && !employees.length} wageCode={server.wageCode} />
                ) : test ? (
                  <>
                    {formulaReads(draft.formula).map((code) => (
                      <PreviewLine key={code} label={code} value={`₹${formatInr(test[code] ?? 0)}`} />
                    ))}
                    <PreviewLine label={`${draft.code} =`} value={`₹${formatInr(test[draft.code])}`} strong divider />
                  </>
                ) : (
                  <Mui.Typography sx={{ ...designType.helperText, color: designColors.slate600 }}>Fill the fields above to test this component.</Mui.Typography>
                )}
              </PreviewBox>
            </Mui.Box>
          </FormSection>
        ) : null}

        <SwitchLine
          checked={draft.status === "Active"}
          onChange={(checked) => set("status")(checked ? "Active" : "Inactive")}
          disabled={locked || required}
          label={`Status: ${draft.status}${required ? ` (required by ${itemLabel})` : ""}`}
        />
      </Mui.Stack>
      <GlobalConfirmDialog
        open={deleteOpen}
        title="Delete component"
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => {
          setDeleteOpen(false);
          onDelete(component);
        }}
        confirmLabel="Delete"
        cancelLabel="Keep"
        maxWidth="sm"
        danger
      >
        Delete {component?.name} ({component?.code}) from {form.name}? Formulas that use it will show an error.
      </GlobalConfirmDialog>
    </GlobalSidePanel>
  );
};

// The server's answer for one real employee: the wage the rule gives
// (with this component), how it was worked out, and the result.
const helper = (text, color = designColors.slate600) => <Mui.Typography sx={{ ...designType.helperText, color }}>{text}</Mui.Typography>;

const ServerTestResult = ({ runnable, test, noEmployees, wageCode = "LWF_WAGE" }) => {
  if (!runnable) return helper("Fill the fields above to test this component.");
  if (noEmployees) return helper("No employees to test on.");
  if (test.loading) return helper("Working it out…");
  if (test.error) return helper(test.error, "#DC2626");
  const data = test.data;
  if (!data) return helper("Pick an employee to test this component.");
  const result = data.result || {};
  const wage = data.wage ?? result.lwf_wage;
  return (
    <Mui.Stack spacing={0.5}>
      {data.note ? <Mui.Typography sx={designType.captionText}>{data.note}</Mui.Typography> : null}
      {wage != null && wage !== "" ? <PreviewLine label={`${wageCode} =`} value={`₹${formatInr(wage)}`} strong divider /> : null}
      {(data.steps || []).map((step) => (
        <Mui.Typography key={step} sx={designType.captionText}>
          {step}
        </Mui.Typography>
      ))}
      <Mui.Typography sx={{ ...designType.tableBodyCell, color: designColors.slate900 }}>{data.sentence || result.label || "—"}</Mui.Typography>
      {(data.warnings || []).map((warning) => (
        <Mui.Typography key={warning} sx={{ ...designType.captionText, color: "#B54708" }}>
          {warning}
        </Mui.Typography>
      ))}
    </Mui.Stack>
  );
};
